import { ANEX, AnexApiClient } from '@anex/common'
import { makeAutoObservable } from 'mobx'
import { RootStore } from './RootStore'
import { TokenManager } from '~/utils/TokenManager'
import {
  IServiceProducts,
  IServiceHistory,
  IOutages,
  IServiceDiagnostics,
} from '~/types'
import { Constants } from '~/utils/Constants'

/**
 * This should include API calls related to the service
 * ie: Outages, Usage, Plan Change, etc...
 */
export class ServiceStore {
  api: AnexApiClient
  serviceHistory: IServiceHistory[] = []
  outages: IOutages = {} as IOutages
  serviceProducts: IServiceProducts = []
  diagnostics: IServiceDiagnostics = {
    tests: [],
    history: [],
  }

  constructor(rootStore: RootStore) {
    this.api = rootStore.api
    makeAutoObservable(this)
  }

  get getOutages() {
    return this.outages
  }

  get getDiagnotics() {
    return this.diagnostics
  }

  *setOutages(data: IOutages) {
    this.outages = yield data
  }

  *setDiagnostics(diagnostics: IServiceDiagnostics) {
    this.diagnostics = yield diagnostics
  }

  *setHistory(histories: IServiceHistory[]) {
    this.serviceHistory = yield histories
  }

  /**
   * Get Contracts
   * @param code
   * @param serviceType
   * @returns
   */
  async getContractByCode(code: string, serviceType: string) {
    if (serviceType === 'uniti') {
      return await this.api.V1.Lookup.Contracts.Broadband.Uniti(
        code,
        TokenManager.getTokenHeader()
      ).then((response) =>
        response.contracts ? response.contracts[0] : undefined
      )
    }
    // default to NBN
    return await this.api.V1.Lookup.Contracts.Broadband.Nbn(
      code,
      TokenManager.getTokenHeader()
    ).then((response) =>
      response.contracts ? response.contracts[0] : undefined
    )
  }

  /**
   * Get possible plans for a service
   * @param serviceCode
   * @param serviceClass
   * @param serviceType
   * @param planType
   * @returns
   */
  async getChangePlans(
    serviceCode: string,
    serviceClass: number,
    serviceType?: string
  ) {
    return await this.api.V1.Account.Actions.ChangeContract.Contracts(
      serviceCode,
      serviceType,
      TokenManager.getTokenHeader()
    )
      .then((response) => {
        if (response.contracts && response.contracts.length > 0) {
          let filteredPlans = response.contracts
          if (Constants.WHOLESALER.settings.code === 'OE') {
            var hiddenPlans = ['12/1', '100/40', '750/50']
            filteredPlans = response.contracts.filter(
              (contract: any) =>
                !(hiddenPlans.some((plan) => contract.name.includes(plan)))
            )
          }

          // Filter out plans for FTTP and HFC service class
          if ([0, 1, 2, 3, 20, 21, 22, 23, 24].includes(serviceClass)) {
            var hiddenPlans = ['100/20', '100/40']
            filteredPlans = filteredPlans.filter((p: any) => {
              return (
                p.serviceType?.toLowerCase() !== 'nbn' ||
                !hiddenPlans.some((plan) => p.name.includes(plan))
              )
            })
          }
          // Filter out the wireless plans if service class is not wireless
          if (![4, 5, 6].includes(serviceClass)) {
            filteredPlans = filteredPlans.filter(
              (plan: any) => !plan.name.includes('FW')
            )
          }
          // Filter out the non-wireless plans if service class is wireless
          if ([4, 5, 6].includes(serviceClass)) {
            filteredPlans = filteredPlans.filter((plan: any) =>
              plan.name.includes('FW')
            )
          }
          return filteredPlans
        }
        return []
      })
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Submit a plan change
   * @param payload
   * @returns
   */
  async postChangePlan(
    payload: ANEX.Website.API.Endpoints.V1.Account.Actions.ChangeContract.ChangeContractRequest
  ) {
    return await this.api.V1.Account.Actions.ChangeContract.Index(
      payload,
      TokenManager.getTokenHeader()
    )
      .then((r) => r)
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Get outages data
   * @param avcID
   */
  async getOutagesData(avcID: string) {
    await this.api.V1.Superloop.Outages.OutageManagement(
      avcID,
      TokenManager.getTokenHeader()
    )
      .then((resp: IOutages) => {
        this.setOutages(resp)
      })
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Get service history
   * @param id Supplier Service ID
   */
  async getServiceHistory(id: string) {
    await this.api.V1.Account.Services.History(
      id,
      TokenManager.getTokenHeader()
    )
      .then(
        (r: ANEX.Website.API.Endpoints.V1.Account.Services.HistoryResponse) => {
          this.setHistory(r.history)
        }
      )
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Get result by diagnostic test ID
   * This is used to get the result of a diagnostic test after it has been run and status is not 'completed
   * @param testId diagnostic test ID
   * @returns
   */
  async getServiceTestResult(testId: string) {
    return await this.api.V1.Superloop.Diagnostics.Result(
      testId,
      TokenManager.getTokenHeader()
    )
      .then((r) => r)
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Reset the VoIP password by customer
   * @param phoneNumber
   * @param accountCode
   * @returns
   */
  async setVoIPResetPassword(
    phoneNumber: string,
    accountCode: string
  ): Promise<boolean> {
    let isSuccess = false
    await this.api.V1.Account.Services.Voip.ResetPassword(
      phoneNumber,
      accountCode,
      TokenManager.getTokenHeader()
    )
      .then(() => {
        //success reset password
        isSuccess = true
      })
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })

    return isSuccess
  }

  /**
   * Get available products to the service
   * @param code
   */
  async getProductsByService(code: string) {
    await this.api.V1.Lookup.Products.Service(
      code,
      '',
      '',
      TokenManager.getTokenHeader()
    )
      .then((r) => (this.serviceProducts = r.products || []))
      .catch((reason: any) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Add product to service
   * @param id
   * @param productCode
   * @returns
   */
  async addProductInService(id: string, productCode: string): Promise<boolean> {
    return await this.api.V1.Account.Actions.AddProduct(
      {
        serviceId: id,
        productCode: productCode,
      },
      TokenManager.getTokenHeader()
    )
      .then((r) => r.success)
      .catch((reason: any) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Retrieve the available diagnostic test types for a service
   * @param supplierServiceId
   * @param supplier
   * @returns
   */
  async getServiceDiagnosticTestTypes(
    supplierServiceId: string,
    supplier: string
  ) {
    // we will try to minimize calls, at least retain in memory the test types
    if (this.diagnostics.tests.length === 0) {
      return await this.api.V1.Superloop.Diagnostics.Index(
        supplierServiceId || '',
        supplier || '',
        TokenManager.getTokenHeader()
      )
        .then((r) => {
          this.setDiagnostics({ ...this.diagnostics, tests: r.testTypes || [] })
          return r.testTypes || []
        })
        .catch((reason) => {
          // we can implement an error logging if needed
          if (import.meta.env.MODE === 'development') {
            console.info(reason)
          }
          throw reason.message
        })
    }
    return this.diagnostics.tests
  }

  /**
   * Get the diagnostic history in ANEX db
   * @param supplierServiceId
   * @param supplier
   * @returns
   */
  async getServiceDiagnosticHistory(
    supplierServiceId: string,
    supplier: string
  ) {
    return this.api.V1.Superloop.Diagnostics.History(
      supplierServiceId || '',
      supplier || '',
      TokenManager.getTokenHeader()
    )
      .then((r) => {
        const newDiagnostics = {
          tests: this.diagnostics.tests,
          history: r || [],
        }
        this.setDiagnostics(newDiagnostics)
        return r || []
      })
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Run a new diagnostic test
   * @param supplierServiceId
   * @param testType
   * @param supplier
   * @returns
   */
  async runServiceDiagnosticTest(
    supplierServiceId: string,
    testType: string,
    supplier: string
  ) {
    return this.api.V1.Superloop.Diagnostics.CreateTest(
      supplierServiceId,
      testType,
      supplier,
      TokenManager.getTokenHeader()
    )
      .then(async () => {
        await this.getServiceDiagnosticHistory(supplierServiceId, supplier)
        return true
      })
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }
}
